import { put, delay, takeEvery, all, call, select, race, cancelled } from "redux-saga/effects";
import _ from "lodash";
import moment from "moment-timezone";
import {
  setPageTopLoader,
  setPictreeGroupSummaryHokuden,
  setHomePictreePhotoShootHokuden,
  setHomePictreeCheckInHokuden,
  setPictreePhotoShootByAssetHokuden,
  setPictreePhotoShootTrendByAssetHokuden,
  patchHomePictreePhotoShootHokuden,
} from "actions";
import { getJwtToken, getDataFromRDS, getDataFromSxi } from "api";
import { PICTREE, ENDPOINT } from "../../constants";
import assetConfig from "../../utils/pictree/AssetConfig";

const SXI_HEAVY_RETRY = {
  timeoutMs: 30000,   // 30s
  retries: 3,         // 合計 4 回までトライ（初回 + リトライ3）
  backoffBase: 1500,  // 1.5s指数バックオフ
};

const USERS_LIGHT_RETRY = {
  timeoutMs: 15000,   // 15s
  retries: 1,
};

const createApprovedBySubtypeQuery = ({ startUtc, endUtc, seasonId, needPad }) => {
  // pad JOIN / 条件は Hokuden と共用シーズンだけ付与
  const padJoin  = needPad ? `JOIN power_asset_detail pad ON rr.power_asset_id = pad.power_asset_id` : ``;
  const padWhere = needPad ? `AND pad.type = 'HEPCO'` : ``;

  return {
    query : `
      SELECT pa.sub_type, COUNT(DISTINCT rr.power_asset_id) AS reviewOkcnt
      FROM review_request_event rre
      JOIN review_request rr ON rre.review_request_id = rr.id
      ${padJoin}
      JOIN power_asset pa ON pa.id = rr.power_asset_id
      WHERE rre.game_space_id = '${seasonId}'
      AND rre.type = 'APPROVED'
      AND rre.occurred_datetime >= '${startUtc}'
      AND rre.occurred_datetime <  '${endUtc}'
      ${padWhere}
      GROUP BY pa.sub_type
      ORDER BY reviewOkcnt DESC;
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

const createApprovedBySubtypeDailyQuery = ({ startUtc, endUtc, seasonId, needPad }) => {
  const padJoin  = needPad ? `JOIN power_asset_detail pad ON rr.power_asset_id = pad.power_asset_id` : ``;
  const padWhere = needPad ? `AND pad.type = 'HEPCO'` : ``;
  return {
    query: `
      SELECT
        DATE(CONVERT_TZ(rre.occurred_datetime, '+00:00', '+09:00')) AS day_jst,
        pa.sub_type,
        COUNT(DISTINCT rr.power_asset_id) AS reviewOkcnt
      FROM review_request_event rre
      JOIN review_request rr ON rre.review_request_id = rr.id
      JOIN power_asset pa ON pa.id = rr.power_asset_id
      ${padJoin}
      WHERE rre.game_space_id = '${seasonId}'
        AND rre.type = 'APPROVED'
        AND rre.occurred_datetime >= '${startUtc}'
        AND rre.occurred_datetime <  '${endUtc}'
        ${padWhere}
      GROUP BY day_jst, pa.sub_type
      ORDER BY day_jst ASC;
    `,
    database: "KpiDashboard",
    type: "list",
  };
};

const enumerateDaysJst = (startJst, endJst) => {
  const days = [];
  let cur = moment.tz(startJst, "Asia/Tokyo");
  const end = moment.tz(endJst, "Asia/Tokyo");
  while (cur.isSameOrBefore(end, "day")) {
    days.push(cur.format("YYYY-MM-DD"));
    cur = cur.add(1, "day");
  }
  return days;
};

function transformDailyPayload(payload, subtypeMap) {
  const result = { date: [] };
  const temp = {};

  // initialize temporary arrays for all possible subtypes
  Object.entries(subtypeMap).forEach(([subType, label]) => {
    temp[label] = [];
  });

  // fill arrays
  payload.forEach((row) => {
    result.date.push(row.date);
    Object.entries(subtypeMap).forEach(([subType, label]) => {
      temp[label].push(row[subType] ?? 0);
    });
  });

  // filter out all-zero arrays
  Object.entries(temp).forEach(([label, arr]) => {
    if (arr.some((v) => v > 0)) {
      result[label] = arr;
    }
  });

  return [result];
}

// タイムアウト＋リトライ
function* callWithTimeoutAndRetry(fn, args = [], opts = {}) {
  const {
    timeoutMs = 20000,           // 1回の待ち時間（APIGW上限より少し短く）
    retries = 2,                 // 失敗時の再試行回数（合計3回＝初回＋2リトライ）
    backoffBase = 1000,          // 1秒
    backoffFactor = 2,           // 2倍ずつ
    jitter = true,               // 少しランダムに待つ
    isRetryable = (err) => {
      const msg = (err && (err.message || err.errorMessage)) || '';
      return /timeout|timed out|504|gateway\s*time-?out/i.test(msg);
    }
  } = opts;

  let attempt = 0;
  while (true) {
    attempt += 1;

    try {
      // 1回の試行にタイムアウトをかける
      const { response, timeout } = yield race({
        response: call(fn, ...args),
        timeout: delay(timeoutMs),
      });

      if (timeout) throw new Error(`timeout after ${timeoutMs}ms`);
      return response; // 成功

    } catch (err) {
      if (attempt > retries || !isRetryable(err)) {
        throw err; // もう無理/リトライ不要
      }
      // 待ってから再試行（指数＋ジッター）
      const wait = Math.min(backoffBase * Math.pow(backoffFactor, attempt - 1), 10000);
      const jitterMs = jitter ? Math.floor(Math.random() * (wait * 0.3)) : 0;
      yield delay(wait + jitterMs);
      // 次のループで再試行
    } finally {
      if (yield cancelled()) {
        // もし fetch/axios なら AbortController を仕込むとさらに良い
      }
    }
  }
}

export const currentPage = (state) => state.page.currentPage;
export const dateRange = (state) => state.page.dateRangePicTreeHokuden;
export const startPrevDate = state => state.page.prevDateRangePicTree[0];
export const endPrevDate = state => state.page.prevDateRangePicTree[1];
export const selectedSeasonHokuden = (state) => state.pictreeHokuden.selectedSeason;

export function* getPictreeGroupSummaryHokuden(action) {
  try {
    yield put(setPageTopLoader(true));
    const jwtToken = yield call(getJwtToken);
    const selectedSeasonData = yield select(selectedSeasonHokuden);
    const seasonId = selectedSeasonData?.[0]?.seasonId;

    if (!seasonId) {
      yield put(setPictreeGroupSummaryHokuden({
        groupCount: 0,
        groupRegisteredCount: 0,
        groupData: [],
      }));
      yield put(setPageTopLoader(false));
      return;
    }

    const sqlQueryGroupSummary = {
      query: `
        SELECT
          pcpg.event_game_group_id AS groupId,
          egg.name AS groupName,
          COUNT(pcpg.player_id) AS playerCount,
          GROUP_CONCAT(DISTINCT pcpg.player_id ORDER BY pcpg.player_id SEPARATOR ', ') AS playerIds,
          MAX(egc.referral_code) AS referralCode
        FROM player_current_participate_group pcpg
        JOIN event_game_group egg
        ON egg.id = pcpg.event_game_group_id
        LEFT JOIN event_game_group_of_custom egc
        ON egc.id = egg.id
        JOIN event_game eg
        ON eg.id = egg.event_game_id
        WHERE eg.game_space_id = '${seasonId}'
        GROUP BY pcpg.event_game_group_id
        ORDER BY playerCount DESC;
      `,
      database: "KpiDashboard",
      type: "list",
    };

    const [summaryTemp] = yield all([
      call(
        getDataFromSxi,
        sqlQueryGroupSummary,
        ENDPOINT.GET_DATA_SXI_DENCHU,
        jwtToken
      ),
    ]);

    const parsedSummary = summaryTemp;
    const groupCount = parsedSummary.length;
    const groupRegisteredCount = parsedSummary.reduce((sum, item) => sum + item.playerCount, 0);
    const groupData = parsedSummary.map(item => ({
      groupId: item.groupId,
      groupName: item.groupName,
      playerCount: item.playerCount,
      playerIds: item.playerIds || "",
      referralCode: item.referralCode || "",
    }));
    const groupSummaryData = {
      groupCount,
      groupRegisteredCount,
      groupData
    };
    yield put(setPictreeGroupSummaryHokuden(groupSummaryData));
    yield put(setPageTopLoader(false));
  } catch (err) {
    console.log(err);
  }
}

function* runEffectsInBatches(effects, { concurrency = 2, gapMs = 300 } = {}) {
  const results = [];
  for (let i = 0; i < effects.length; i += concurrency) {
    const batch = effects.slice(i, i + concurrency);
    // 同時に投げるのは batch サイズ分だけ
    const batchResults = yield all(batch);
    results.push(...batchResults);

    // 次バッチの前にクールダウン（最後のバッチは不要）
    if (i + concurrency < effects.length && gapMs > 0) {
      yield delay(gapMs);
    }
  }
  return results;
}

export function* getPictreeDataHomePhotoShootHokuden(action) {
  try {
    yield put(setPageTopLoader(true));

    const jwtToken = yield call(getJwtToken);
    const rangeTarget = yield select(dateRange);

    const startUtc = moment.tz(`${rangeTarget[0]} 00:00:00`, "Asia/Tokyo").utc().format("YYYY-MM-DD HH:mm:ss");
    const endUtc = moment
      .tz(`${rangeTarget[1]} 00:00:00`, "Asia/Tokyo")
      .add(1, "day")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");

    const seasonSelectedHokuden = yield select(selectedSeasonHokuden);
    if (!seasonSelectedHokuden?.length) {
      // 初期化のみ
      yield put(setHomePictreePhotoShootHokuden({
        countDenchu: 'loading',
        countUniqueUser: 'loading',
        countNewUser: undefined,
        countPic: 'loading',
        countAsset: 'loading',
        countDupeDenchu: 'loading',
        countReviewOk: 'loading',
        progressDenchu: { taken: 0, max: 0, maxByasset: 0 },
        timings: {}, errors: {},
      }));
      yield put(setPageTopLoader(false));
      return;
    }

    const seasonQuery = (() => {
      if (
        seasonSelectedHokuden.length > 0 &&
        seasonSelectedHokuden[0].seasonId !== undefined
      ) {
        const ids = seasonSelectedHokuden
          .map((s) => `'${s.seasonId}'`)
          .join(", ");
        // 長さが１なら単一、複数なら IN
        return seasonSelectedHokuden.length === 1
          ? `AND (gameSpaceId = ${ids})`         // ids は "'id1'"
          : `AND (gameSpaceId IN (${ids}))`;     // ids は "'id1','id2',…"
      }
      return "";
    })();

    // 初回ロード（全部loading）
    yield put(setHomePictreePhotoShootHokuden({
      countDenchu: 'loading',
      countUniqueUser: 'loading',
      countNewUser: undefined,
      countPic: 'loading',
      countAsset: 'loading',
      countDupeDenchu: 'loading',
      countReviewOk: 'loading',
      progressDenchu: { taken: 0, max: 0, maxByasset: 0 },
      timings: {}, errors: {},
    }));

    let maxNumber = 1614500;

    const sqlQueryCountPhotoDenchu = {
      "query": `
        SELECT COUNT(DISTINCT pae.power_asset_id) AS total_photo_power_assets
        FROM power_asset_event pae
        INNER JOIN power_asset_detail pad ON pae.power_asset_id = pad.power_asset_id
        WHERE pae.game_space_id = '${seasonSelectedHokuden[0].seasonId}'
          AND pae.type = 'PHOTOGRAPH'
          AND pad.type = 'HEPCO'
          AND pae.occurred_datetime >= '${startUtc}'
          AND pae.occurred_datetime <  '${endUtc}'
      `,
      "database": "KpiDashboard",
      "type": "list"
    };
    // const sqlQueryCountPhotoDenchu = {
    //   "query": `
    //     SELECT COUNT(DISTINCT rr.power_asset_id) AS total_photo_power_assets
    //     FROM review_request_event rre
    //     JOIN review_request rr ON rre.review_request_id = rr.id
    //     JOIN power_asset_detail pad ON rr.power_asset_id = pad.power_asset_id
    //     WHERE rre.occurred_datetime >= '${startUtc}'
    //     AND rre.occurred_datetime <  '${endUtc}'
    //     AND rre.game_space_id = '${seasonSelectedHokuden[0].seasonId}'
    //     AND pad.type = 'HEPCO';
    //   `,
    //   "database": "KpiDashboard",
    //   "type": "list"
    // };
    const sqlQueryCountReviewOk = {
      query: `
        SELECT COUNT(DISTINCT rr.power_asset_id) AS reviewOk_count
        FROM review_request_event rre
        JOIN review_request rr ON rre.review_request_id = rr.id
        JOIN power_asset_detail pad ON rr.power_asset_id = pad.power_asset_id
        WHERE rre.game_space_id = '${seasonSelectedHokuden[0].seasonId}'
        AND rre.type = 'APPROVED'
        AND rre.occurred_datetime >= '${startUtc}'
        AND rre.occurred_datetime <  '${endUtc}'
        AND pad.type = 'HEPCO';
      `,
      database: 'KpiDashboard',
      type: 'list'
    };
    const sqlQueryCountUniqueUser = {
      "query":  `
        SELECT COUNT(DISTINCT rr.player_id) AS unique_photographers
        FROM review_request_event rre
        JOIN review_request rr ON rr.id = rre.review_request_id
        JOIN review_request_event_of_approved rreoa ON rreoa.id = rre.id
        JOIN power_asset_detail pad ON rr.power_asset_id = pad.power_asset_id
        WHERE rre.game_space_id = '${seasonSelectedHokuden[0].seasonId}'
        AND rre.type = 'APPROVED'
        AND rre.occurred_datetime >= '${startUtc}'
        AND rre.occurred_datetime <  '${endUtc}'
        AND pad.type = 'HEPCO';
      `,
      "database": "KpiDashboard",
      "type": "list"
    };
    const sqlQueryCountPicCount = {
      "query": `
        SELECT
          SUM(picCount) AS total_pic_count
        FROM (
          SELECT picCount FROM DenchuPhotoShootDenchuEvtProd WHERE CONVERT_TZ(FROM_UNIXTIME(logDate / 1000), 'UTC', 'Asia/Tokyo') BETWEEN '${rangeTarget[0]} 00:00:00' AND '${rangeTarget[1]} 23:59:59' AND property = 'hepco' ${seasonQuery}
        ) AS combined_data;`,
      "database": "KpiDashboard",
      "type": "list"
    };

    // dupe photoshoot - denchu
    const sqlQueryCountPhotoDupeDenchu = {
      "query": `
        SELECT COUNT(*) AS dupe_asset_count
        FROM (
          SELECT pae.power_asset_id
          FROM power_asset_event AS pae
          JOIN power_asset_detail AS pad
            ON pad.power_asset_id = pae.power_asset_id
          WHERE pae.type = 'PHOTOGRAPH'
            AND pad.type = 'HEPCO'
            AND pae.game_space_id = '${seasonSelectedHokuden[0].seasonId}'
            AND pae.occurred_datetime >= '${startUtc}'
            AND pae.occurred_datetime <  '${endUtc}'
          GROUP BY pae.power_asset_id
          HAVING COUNT(*) > 1
        ) AS t;
      `,
      "database": "KpiDashboard",
      "type": "list"
    };

    const sqlQueryCountNewUsers = {
      query: `
        SELECT COUNT(*) AS first_time_photographers
        FROM (
          SELECT DISTINCT rr.player_id
          FROM review_request_event rre
          JOIN review_request rr ON rr.id = rre.review_request_id
          JOIN review_request_event_of_approved rreoa ON rreoa.id = rre.id
          JOIN review_request_photo_bind bind ON bind.review_request_id = rr.id
          WHERE rre.game_space_id = '${seasonSelectedHokuden[0].seasonId}'
            AND rre.type = 'APPROVED'
            AND rre.occurred_datetime BETWEEN
                  CONVERT_TZ('${rangeTarget[0]} 00:00:00','Asia/Tokyo','UTC')
              AND CONVERT_TZ('${rangeTarget[1]} 23:59:59','Asia/Tokyo','UTC')
        ) AS c
        WHERE NOT EXISTS (
          SELECT 1
          FROM review_request_event pre
          JOIN review_request rr_pre ON rr_pre.id = pre.review_request_id
          WHERE rr_pre.player_id = c.player_id
            AND pre.type = 'APPROVED'
            AND pre.occurred_datetime <
                  CONVERT_TZ('${rangeTarget[0]} 00:00:00','Asia/Tokyo','UTC')
        );
      `,
      database: 'KpiDashboard',
      type: 'list'
    };

    // ========= 1) 優先バッチ =========
    const [
      dataCountReviewOk,
      dataPhotoDenchu,
    ] = yield all([
      call(
        callWithTimeoutAndRetry,
        getDataFromSxi,
        [sqlQueryCountReviewOk, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
        SXI_HEAVY_RETRY
      ),
      call(
        callWithTimeoutAndRetry,
        getDataFromRDS,
        [sqlQueryCountPhotoDenchu, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
        SXI_HEAVY_RETRY
      ),
    ]);

    // 先に描画
    const countReviewOk = dataCountReviewOk[0]["reviewOk_count"];
    const countPhotoDenchuValue = dataPhotoDenchu[0]["total_photo_power_assets"];

    yield put(setHomePictreePhotoShootHokuden({
      countDenchu: countPhotoDenchuValue,
      countAsset : countPhotoDenchuValue,
      countReviewOk,
      countUniqueUser: 'loading',
      countNewUser: undefined,
      countPic: 'loading',
      countDupeDenchu: 'loading',
      progressDenchu: { taken: countReviewOk, max: maxNumber, maxByasset: maxNumber },
      timings: {}, errors: {},
    }));

    // ========= 2) 次バッチ（並列2本） =========
    const [dataCountUniue] = yield all([
      call(
        callWithTimeoutAndRetry,
        getDataFromRDS,
        [sqlQueryCountUniqueUser, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
        SXI_HEAVY_RETRY
      ),
    ]);
    const countUniqueUserValue = dataCountUniue[0]["unique_photographers"];
    yield put(patchHomePictreePhotoShootHokuden({
      countUniqueUser: countUniqueUserValue,
    }));

    // ========= 3) 最後のバッチ（重複：1本だけ）=========
    const dataDupePhotoDenchu = yield call(
      callWithTimeoutAndRetry,
      getDataFromRDS,
      [sqlQueryCountPhotoDupeDenchu, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
      SXI_HEAVY_RETRY
    );
    const countDupeDenchuCountValue = dataDupePhotoDenchu[0]["dupe_asset_count"];
    yield put(patchHomePictreePhotoShootHokuden({ countDupeDenchu: countDupeDenchuCountValue }));

    const dataPicCountResp = yield call(
      callWithTimeoutAndRetry,
      getDataFromRDS,
      [sqlQueryCountPicCount, ENDPOINT.GET_DATA_USERS, jwtToken],
      USERS_LIGHT_RETRY
    );
    const picStr = JSON.parse(dataPicCountResp.body)?.[0]?.[0]?.stringValue;
    const countPicNum = picStr == null ? 0 : parseInt(picStr, 10, 10);
    yield put(patchHomePictreePhotoShootHokuden({ countPic: countPicNum }));

    yield put(setPageTopLoader(false));

  } catch (err) {
    console.log(err);
  }
}

export function* getPictreeDataHomeCheckInHokudenFromSxi(action) {
  try {
    yield put(setPageTopLoader(true));
    const jwtToken = yield call(getJwtToken);
    const rangeTarget = yield select(dateRange);
    const selectedSeasonData = yield select(selectedSeasonHokuden);

    if (!rangeTarget || rangeTarget.length !== 2) {
      console.error("Invalid rangeTarget");
      yield put(setPageTopLoader(false));
      return;
    }

    if (!selectedSeasonData || selectedSeasonData.length === 0) {
      console.error("No seasons selected");
      yield put(setPageTopLoader(false));
      return;
    }
    const seasonIdList = selectedSeasonData.map(season => `'${season.seasonId}'`).join(", ");

    const sqlQueryCheckinSummary = {
      query: `
        SELECT COUNT(*) AS COUNT,
        COUNT(DISTINCT pae.power_asset_id) AS ASSET_COUNT,
        COUNT(DISTINCT pae.player_id) AS PLAYER_COUNT,
        MAX(ps.name) AS MOST_CHECKIN,
        MAX(pae.power_asset_id) AS MOST_ASSET
        FROM power_asset_event pae
        JOIN player_state ps ON pae.player_id = ps.player_id
        JOIN power_asset_detail pad ON pae.power_asset_id = pad.power_asset_id
        WHERE CONVERT_TZ(pae.occurred_datetime, 'UTC', 'Asia/Tokyo')
        BETWEEN '${rangeTarget[0]} 00:00:00' AND '${rangeTarget[1]} 23:59:59' AND pae.type in ("CHECK_IN")
        AND pae.game_space_id IN (${seasonIdList})
        AND pad.type = 'HEPCO'
      `,
      database: "KpiDashboard",
      type: "list",
    };

    const [summaryTemp] = yield all([
      call(
        getDataFromSxi,
        sqlQueryCheckinSummary,
        ENDPOINT.GET_DATA_SXI_DENCHU,
        jwtToken
      ),
    ]);

    const homeCheckInData = {
        checkInCount: summaryTemp[0]["COUNT"],
        checkInCountKisu: summaryTemp[0]["ASSET_COUNT"],
        checkInCountUniqueUser: summaryTemp[0]["PLAYER_COUNT"],
        checkInMost: summaryTemp[0]["MOST_ASSET"],
        checkInMostUser: summaryTemp[0]["MOST_CHECKIN"],
        checkInRatio: "--", //checkInRatio,
      };

    yield put(setHomePictreeCheckInHokuden(homeCheckInData));
    yield put(setPageTopLoader(false));
  } catch (err) {
    console.log(err);
  }
}

export function* getPictreeDataPhotoShootByAsset(action) {
  try {
    yield put(setPageTopLoader(true));

    const jwtToken = yield call(getJwtToken);
    const rangeTarget = yield select(dateRange);
    const seasonSelected = yield select(selectedSeasonHokuden);
    const seasonId = seasonSelected?.[0]?.seasonId || null;
    const needPad = (seasonId === "cmb68qvei00011vo0e591229z");

    const startUtc = moment
      .tz(`${rangeTarget[0]} 00:00:00`, "Asia/Tokyo")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");
    const endUtc = moment
      .tz(`${rangeTarget[1]} 00:00:00`, "Asia/Tokyo")
      .add(1, "day")
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");

    // プレースホルダ
    yield put(
      setPictreePhotoShootByAssetHokuden([
        { subType: "loading", CNT: 0, label: "-" },
      ])
    );

    const singleSql = createApprovedBySubtypeQuery({
      startUtc,
      endUtc,
      seasonId,
      needPad,
    });

    const sxiEffects = [
      call(
        callWithTimeoutAndRetry,
        getDataFromRDS,
        [singleSql, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
        SXI_HEAVY_RETRY
      ),
    ];

    const [rows] = yield call(runEffectsInBatches, sxiEffects, {
      concurrency: 2,
      gapMs: 300,
    });

    // ---- 整形：定義順を維持し、欠損は0 ----
    const keys = Object.keys(assetConfig.assetSubType);
    const bySubtype = new Map();
    (rows || []).forEach(({ sub_type, reviewOkcnt }) =>
      bySubtype.set(sub_type, Number(reviewOkcnt) || 0)
    );

    const payload = keys
      .map((k) => ({
        subType: k,
        CNT: bySubtype.get(k) ?? 0,
        label: assetConfig.assetSubType[k],
      }))
      // CNTが0のものを除外
      .filter(item => item.CNT > 0);

    yield put(setPictreePhotoShootByAssetHokuden(payload));
  } catch (err) {
    console.log("[getPictreeDataPhotoShootByAsset] ERROR", err);
  } finally {
    yield put(setPageTopLoader(false));
  }
}

export function* getPictreeDataPhotoShootTrendByAsset(action) {
  try {
    yield put(setPageTopLoader(true));

    const jwtToken = yield call(getJwtToken);
    const rangeTarget = yield select(dateRange);          // ["YYYY-MM-DD","YYYY-MM-DD"]（JST）
    const seasonSelected = yield select(selectedSeasonHokuden);
    const seasonId = seasonSelected?.[0]?.seasonId || null;
    const needPad = (seasonId === "cmb68qvei00011vo0e591229z");

    // JST日の境界 → UTC へ
    const startUtc = moment.tz(`${rangeTarget[0]} 00:00:00`, "Asia/Tokyo").utc().format("YYYY-MM-DD HH:mm:ss");
    const endUtc   = moment.tz(`${rangeTarget[1]} 00:00:00`, "Asia/Tokyo").add(1, "day").utc().format("YYYY-MM-DD HH:mm:ss");

    const dailySql = createApprovedBySubtypeDailyQuery({ startUtc, endUtc, seasonId, needPad });

    const [rows] = yield call(runEffectsInBatches, [
      call(
        callWithTimeoutAndRetry,
        getDataFromRDS,
        [dailySql, ENDPOINT.GET_DATA_SXI_DENCHU, jwtToken],
        SXI_HEAVY_RETRY
      ),
    ]);

    // ---- 整形：日付×サブタイプ ----
    const subtypeKeys = Object.keys(assetConfig.assetSubType);
    const allDays = enumerateDaysJst(rangeTarget[0], rangeTarget[1]);

    // day => (subtype => count)
    const byDay = new Map();
    (rows || []).forEach(({ day_jst, sub_type, reviewOkcnt }) => {
      const dayKey = moment.tz(day_jst, "Asia/Tokyo").format("YYYY-MM-DD"); // ★ここ
      if (!byDay.has(dayKey)) byDay.set(dayKey, new Map());
      byDay.get(dayKey).set(sub_type, Number(reviewOkcnt) || 0);
    });

    // 出力： [{ date, NORMAL, WITH_PLANTS, HAS_PROTECTIVE_TUBE }]
    const payload = allDays.map((d) => {
      const row = { date: d };
      const cell = byDay.get(d) || new Map();
      subtypeKeys.forEach((k) => {
        row[k] = cell.get(k) || 0;
      });
      return row;
    });

    const payload2 = transformDailyPayload(payload, assetConfig.assetSubType);

    yield put(setPictreePhotoShootTrendByAssetHokuden(payload2));
  } catch (err) {
    console.log("[getPictreeDataPhotoShootTrendByAsset] ERROR", err);
  } finally {
    yield put(setPageTopLoader(false));
  }
}

export default function* watchPageSettings() {
  yield takeEvery(PICTREE.GET_GROUP_SUMMARY_HOKUDEN, getPictreeGroupSummaryHokuden);
  yield takeEvery(PICTREE.GET_HOME_PHOTOSHOOT_HOKUDEN, getPictreeDataHomePhotoShootHokuden);
  yield takeEvery(PICTREE.GET_HOME_CHECKIN_HOKUDEN, getPictreeDataHomeCheckInHokudenFromSxi);
  yield takeEvery(PICTREE.GET_PHOTOSHOOT_BY_ASSET_HOKUDEN, getPictreeDataPhotoShootByAsset);
  yield takeEvery(PICTREE.GET_PHOTOSHOOT_TREND_BY_ASSET_HOKUDEN, getPictreeDataPhotoShootTrendByAsset);
}
